import { useEffect, useState } from "react";
import axios from "axios";
import { X, RefreshCw, ExternalLink, Check, AlertCircle, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Studio-only "FB Share Preview" modal.
 *
 *  Shows the EXACT 1200×630 image Facebook / WhatsApp / Twitter will
 *  display when this story is shared — fetched live from the
 *  `/api/og-image/{slug}.png` endpoint. Lets the operator visually
 *  confirm the cover is correct BEFORE clicking Share, so a mismatched
 *  thumbnail (e.g. the wrong story's first frame baked into the share
 *  card) is caught here instead of on Facebook where the preview is
 *  locked into the post and the only fix is delete + repost.
 *
 *  Also exposes a "Refresh Facebook cache" button: calls
 *  POST /api/admin/facebook/refresh-og/{id} which programmatically
 *  hits Facebook's Sharing Debugger so FB re-scrapes the preview.
 *  Requires FB_APP_ID + FB_APP_SECRET in the backend env — without
 *  them the button is still visible but flips to a friendly "configure
 *  first" hint instead of throwing.
 */
export default function FBPreviewModal({ video, onClose }) {
  const [adminToken] = useState(
    () => (typeof window !== "undefined"
      && window.localStorage.getItem("storylo_admin_token")) || "",
  );
  // Per-mount cache-bust so we always fetch a FRESH copy of the og
  // image from our own backend (avoiding the operator's browser cache
  // serving them yesterday's image while they're debugging today).
  const [imgBust] = useState(() => Date.now());
  const slug = video?.slug;
  const ogImageUrl = slug
    ? `${API.replace(/\/api$/, "")}/api/og-image/${slug}.png?lang=en&t=${imgBust}`
    : "";
  const shareUrl = slug
    ? `${API.replace(/\/api$/, "")}/api/share/${slug}`
    : "";

  const [refreshState, setRefreshState] = useState({ status: "idle" });
  const [imgError, setImgError] = useState(false);

  const refreshFBCache = async () => {
    setRefreshState({ status: "loading" });
    try {
      const r = await axios.post(
        `${API}/admin/facebook/refresh-og/${video.id}`,
        {},
        { headers: { "X-Admin-Token": adminToken } },
      );
      setRefreshState({
        status: "success",
        scrapedTitle: r.data.title,
        scrapedImage: r.data.image,
      });
    } catch (e) {
      setRefreshState({
        status: "error",
        message: e?.response?.data?.detail || e.message || "Refresh failed",
      });
    }
  };

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!video) return null;

  const debuggerUrl = shareUrl
    ? `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(shareUrl)}`
    : "https://developers.facebook.com/tools/debug/";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="fb-preview-modal-backdrop"
    >
      <div
        className="w-full max-w-2xl bg-[color:var(--panel)] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="fb-preview-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[color:var(--panel)] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1877F2]/15 border border-[#1877F2]/30 flex items-center justify-center">
              <Facebook className="w-5 h-5 text-[#5099f5]" />
            </div>
            <div>
              <div className="text-sm font-semibold">
                Facebook share preview
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                Exactly what FB · WhatsApp · Twitter will show
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center"
            data-testid="fb-preview-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Mock FB post card */}
          <div className="rounded-lg overflow-hidden border border-white/10 bg-[#1a1a1f]">
            <div className="aspect-[1200/630] w-full bg-black flex items-center justify-center">
              {imgError ? (
                <div className="text-center text-xs text-[color:var(--muted)] p-6">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2 text-yellow-400" />
                  Could not load the share-card image.
                  <br />
                  Endpoint:{" "}
                  <code className="break-all text-[10px]">{ogImageUrl}</code>
                </div>
              ) : (
                <img
                  src={ogImageUrl}
                  alt={`Share preview for ${video.title}`}
                  className="w-full h-full object-cover"
                  data-testid="fb-preview-image"
                  onError={() => setImgError(true)}
                />
              )}
            </div>
            <div className="px-3 py-2 bg-[#242528] text-xs">
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--muted)]">
                storylo.co.uk
              </div>
              <div className="font-semibold mt-0.5 text-white">
                {video.title} · Storylo
              </div>
            </div>
          </div>

          {/* Helpful note */}
          <div className="text-xs text-[color:var(--muted)] leading-relaxed">
            This image is fetched live from your server right now. If it
            looks correct here but FB still shows the wrong cover on
            existing posts, those posts are locked to FB&apos;s older cache —
            delete the post and re-share, or click <strong>Refresh FB
            cache</strong> below first.
          </div>

          {/* Refresh feedback */}
          {refreshState.status === "success" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-xs text-green-100">
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <div data-testid="fb-preview-refresh-success">
                Facebook re-scraped your story. Future shares will use the
                fresh preview above.
                {refreshState.scrapedImage && (
                  <div className="mt-1 text-[10px] text-green-100/70 break-all">
                    FB now cached: {refreshState.scrapedImage}
                  </div>
                )}
              </div>
            </div>
          )}
          {refreshState.status === "error" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-100">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div data-testid="fb-preview-refresh-error">
                <div className="font-semibold mb-1">Couldn&apos;t refresh FB cache automatically.</div>
                <div className="text-yellow-100/80">{refreshState.message}</div>
                <div className="mt-2 text-yellow-100/80">
                  Fallback: open the FB Sharing Debugger and click
                  &ldquo;Scrape Again&rdquo; manually.
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              onClick={refreshFBCache}
              disabled={refreshState.status === "loading"}
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="fb-preview-refresh-cache"
            >
              <RefreshCw className={`w-4 h-4 ${refreshState.status === "loading" ? "animate-spin" : ""}`} />
              {refreshState.status === "loading" ? "Asking Facebook to re-scrape…" : "Refresh FB cache"}
            </button>
            <a
              href={debuggerUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm inline-flex items-center gap-2"
              data-testid="fb-preview-open-debugger"
            >
              Open FB Sharing Debugger
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={onClose}
              className="btn-ghost text-sm ml-auto"
              data-testid="fb-preview-done"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
